import React, { useState, useEffect } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import styled from 'styled-components';
import { navDelay, loaderDelay } from '@utils';
import { usePrefersReducedMotion } from '@hooks';

const StyledHeroSection = styled.section`
  ${({ theme }) => theme.mixins.flexCenter};
  flex-direction: column;
  align-items: flex-start;
  min-height: 100vh;
  height: 100vh;
  padding: 0;

  @media (max-height: 700px) and (min-width: 700px), (max-width: 360px) {
    height: auto;
    padding-top: var(--nav-height);
  }

  h1 {
    margin: 0 0 30px 4px;
    color: var(--green);
    font-family: var(--font-mono);
    font-size: clamp(var(--fz-sm), 5vw, var(--fz-md));
    font-weight: 400;

    @media (max-width: 480px) {
      margin: 0 0 20px 2px;
    }
  }

  h3 {
    margin-top: 5px;
    color: var(--slate);
    line-height: 0.9;
  }

  .stats-container {
    display: flex;
    gap: 2rem;
    margin: 2rem 0;

    .stat-item {
      text-align: center;
      padding: 1rem 2rem;
      background: var(--light-navy);
      border-radius: 8px;
      transition: transform 0.2s ease;

      &:hover {
        transform: translateY(-5px);
      }

      span {
        display: block;

        &.stat-number {
          color: var(--green);
          font-size: var(--fz-xxl);
          font-weight: 600;
          margin-bottom: 0.5rem;
        }

        &.stat-label {
          color: var(--slate);
          font-size: var(--fz-sm);
          font-family: var(--font-mono);
        }
      }
    }
  }

  .email-link {
    ${({ theme }) => theme.mixins.bigButton};
    margin-top: 50px;
  }
`;

const Hero = () => {
  const [isMounted, setIsMounted] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    const timeout = setTimeout(() => setIsMounted(true), navDelay);
    return () => clearTimeout(timeout);
  }, []);

  const one = <h1>Hi, my name is</h1>;
  const two = <h2 className="big-heading">Shovan Das.</h2>;
  const three = (
    <h3 className="medium-heading">I write code that’s clean, reliable & built for impact.</h3>
  );
  const four = (
    <div className="stats-container">
      <div className="stat-item">
        <span className="stat-number">8</span>
        <span className="stat-label">Years</span>
      </div>
      <div className="stat-item">
        <span className="stat-number">10</span>
        <span className="stat-label">Clients</span>
      </div>
      <div className="stat-item">
        <span className="stat-number">100+</span>
        <span className="stat-label">Projects</span>
      </div>
    </div>
  );
  const five = (
    <a className="email-link" href="/#projects" rel="noreferrer">
      View Portfolio
    </a>
  );

  const items = [one, two, three, four, five];

  return (
    <StyledHeroSection>
      {prefersReducedMotion ? (
        <>
          {items.map((item, i) => (
            <div key={i}>{item}</div>
          ))}
        </>
      ) : (
        <TransitionGroup component={null}>
          {isMounted &&
            items.map((item, i) => (
              <CSSTransition key={i} classNames="fadeup" timeout={loaderDelay}>
                <div style={{ transitionDelay: `${i + 1}00ms` }}>{item}</div>
              </CSSTransition>
            ))}
        </TransitionGroup>
      )}
    </StyledHeroSection>
  );
};

export default Hero;
